import { APP_NAME, MIN_PASSWORD_LENGTH } from '@foosball/shared';
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { ApiError, api } from '../api/client';
import { Button, Card, Field, FormError } from '../components/ui';

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirmation) {
      setError('Those passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/auth/reset-password', { token, newPassword: password });
      setDone(true);
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not reset the password. Try again.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
        <Card>
          <h1 className="text-lg font-semibold">Link incomplete</h1>
          <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
            This page needs the token from your reset email. Open the link from the email itself,
            or request a new one.
          </p>
          <p className="mt-4 text-sm">
            <Link
              to="/forgot-password"
              className="font-medium text-emerald-700 hover:underline dark:text-emerald-400"
            >
              Request a new link
            </Link>
          </p>
        </Card>
      </main>
    );
  }

  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-4 py-10">
      <h1 className="mb-1 text-3xl font-bold tracking-tight">Choose a new password</h1>
      <p className="mb-6 text-sm text-slate-500 dark:text-slate-400">{APP_NAME}</p>

      <Card>
        {done ? (
          <div role="status">
            <p className="text-sm">Your password has been changed.</p>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Any other devices signed in to your account have been signed out.
            </p>
            <div className="mt-4">
              <Button type="button" onClick={() => void navigate('/login')}>
                Sign in
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <FormError message={error} />

            <Field
              label="New password"
              type="password"
              name="newPassword"
              autoComplete="new-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              hint={`At least ${MIN_PASSWORD_LENGTH} characters`}
            />

            <Field
              label="Confirm new password"
              type="password"
              name="confirmPassword"
              autoComplete="new-password"
              required
              value={confirmation}
              onChange={(e) => setConfirmation(e.target.value)}
            />

            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving…' : 'Set new password'}
            </Button>
          </form>
        )}
      </Card>

      {!done && (
        <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
          <Link
            to="/forgot-password"
            className="font-medium text-emerald-700 hover:underline dark:text-emerald-400"
          >
            Request a new link
          </Link>
        </p>
      )}
    </main>
  );
}
